import { useEffect, type FC } from 'react'
import { useTranslation } from 'react-i18next'

import { WidgetType } from '@pages/IntroducingBroker/DashboardPoC/types'
import type { Widget } from '@pages/IntroducingBroker/DashboardPoC/types'
import { widgetRegistry } from '@pages/IntroducingBroker/DashboardPoC/widgetRegistry'

import { CommonPaths } from '@utils/path'

import { Box } from '@shared/components/Box'
import { usePageNotice } from '@shared/components/PageNotice/PageNoticeContext'

interface IBDashboardPageProps {
  widgets: Widget[]
  isMobile: boolean
}

export const IBDashboardFixedLayoutPage: FC<IBDashboardPageProps> = ({ widgets, isMobile }) => {
  const { t } = useTranslation()
  const { addNotice } = usePageNotice()

  useEffect(() => {
    addNotice.attention({
      id: 'dashboard-disclaimer',
      canClose: true,
      title: t('IB.Dashboard.Disclaimer.title'),
      message: t('IB.Dashboard.Disclaimer.text'),
      paths: [CommonPaths.IntroducingBrokerDashboard],
      dismissType: 'temporary',
      dismissDuration: 14,
    })
  }, [addNotice, t])

  const renderWidget = (type: WidgetType) => {
    if (!widgets.some((widget) => widget.type === type)) {
      return null
    }

    const Component = widgetRegistry[type].component

    return <Component />
  }

  return (
    <Box flex direction='column' gap={isMobile ? 'x24' : 'x32'} my={isMobile ? 'x24' : 'x32'}>
      {renderWidget(WidgetType.IB_METRICS)}

      <Box
        flex
        gap={isMobile ? 'x24' : 'x40'}
        align='flex-start'
        direction={isMobile ? 'column' : 'row'}
      >
        <Box
          flex
          direction='column'
          gap={isMobile ? 'x28' : 'x32'}
          minWidth={0}
          flexItem={isMobile ? undefined : '1 1 57.62%'}
          maxWidth={isMobile ? undefined : '57.62%'}
          fullWidth={isMobile}
        >
          {renderWidget(WidgetType.IB_WALLETS)}
          {renderWidget(WidgetType.IB_TRANSACTION_HISTORY)}
          {!isMobile && renderWidget(WidgetType.QUICK_PERFORMANCE)}
          {!isMobile && renderWidget(WidgetType.IB_CJM)}
          {!isMobile && renderWidget(WidgetType.IB_REBATES_MANAGEMENT)}
        </Box>

        <Box
          flex
          direction='column'
          gap={isMobile ? 'x28' : 'x32'}
          minWidth={0}
          flexItem={isMobile ? undefined : '1 1 38.9%'}
          maxWidth={isMobile ? undefined : '38.9%'}
          fullWidth={isMobile}
        >
          {renderWidget(WidgetType.PAYOUT_SCHEME)}
          {renderWidget(WidgetType.LOYALTY_PROGRAM)}
          {renderWidget(WidgetType.REFERRAL_LINK)}
          {isMobile && renderWidget(WidgetType.QUICK_PERFORMANCE)}
          {isMobile && renderWidget(WidgetType.IB_CJM)}
          {isMobile && renderWidget(WidgetType.IB_REBATES_MANAGEMENT)}
        </Box>
      </Box>
    </Box>
  )
}
